import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Card from 'components/card/Card';
import {
  Button,
  Flex,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  PaginationState,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Event } from 'services/@types';
import EventService from 'services/event.api';
import { useNavigate } from 'react-router-dom';
import useColumns from './hooks/useColumns';
import { buildEventParams } from './buildEventParams';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_INDEX = 1;

export default function EventsOverview() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const navigate = useNavigate();

  const [events, setEvents] = useState<Event[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('dateAndTime:desc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const searchRef = useRef<HTMLInputElement>(null);

  const pages = useMemo(() => {
    const arr = [];
    for (let i = 1; i <= totalPages; i++) arr.push(i);
    return arr;
  }, [totalPages]);

  const columns = useColumns();

  const table = useReactTable({
    data: events,
    columns,
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  const fetchEvents = useCallback(async () => {
    try {
      const params = buildEventParams({ pageSize, pageIndex, sortBy, searchQuery, dateFrom, dateTo });
      const data = await EventService.getEvents(params);
      if (data?.results) {
        setEvents(data.results);
        setTotalPages(data.totalPages);
      }
    } catch (error) {
      console.error(error);
    }
  }, [pageSize, pageIndex, sortBy, searchQuery, dateFrom, dateTo]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return (
    <Flex direction="column" pt={{ sm: '125px', lg: '75px' }}>
      <Card px="0px">
        <Flex direction="column" w="100%" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
          <Flex px="25px" justify="space-between" align="center" mb="8px">
            <Text color={textColor} fontSize="22px" fontWeight="700">
              Events
            </Text>
          </Flex>
          <Flex px="25px" gap="8px" mb="16px" wrap="wrap" align="center">
            <Input
              ref={searchRef}
              placeholder="Search by event name..."
              size="sm"
              borderRadius="8px"
              w="220px"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSearchQuery(searchRef.current?.value || '');
                  setPagination((prev) => ({ ...prev, pageIndex: DEFAULT_PAGE_INDEX }));
                }
              }}
            />
            <Button
              size="sm"
              variant="brand"
              borderRadius="8px"
              onClick={() => {
                setSearchQuery(searchRef.current?.value || '');
                setPagination((prev) => ({ ...prev, pageIndex: DEFAULT_PAGE_INDEX }));
              }}
            >
              Search
            </Button>
            <Select
              size="sm"
              w="180px"
              borderRadius="8px"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="dateAndTime:desc">Newest First</option>
              <option value="dateAndTime:asc">Oldest First</option>
              <option value="name:asc">Name A–Z</option>
              <option value="name:desc">Name Z–A</option>
            </Select>
            <Input
              type="date"
              size="sm"
              w="150px"
              borderRadius="8px"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPagination((prev) => ({ ...prev, pageIndex: DEFAULT_PAGE_INDEX }));
              }}
            />
            <Input
              type="date"
              size="sm"
              w="150px"
              borderRadius="8px"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPagination((prev) => ({ ...prev, pageIndex: DEFAULT_PAGE_INDEX }));
              }}
            />
          </Flex>
          <Table variant="simple" color="gray.500" mb="24px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id} borderColor={borderColor}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr
                  key={row.id}
                  cursor="pointer"
                  onClick={() => navigate(`/admin/events/detail/${(row.original as Event).id}`)}
                  _hover={{ bg: 'gray.50' }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id} borderColor={borderColor}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
          {events.length === 0 && (
            <Text textAlign="center" color="gray.400" py="20px">
              No events found.
            </Text>
          )}
          <Flex px="25px" pb="20px" justify="space-between" align="center">
            <Flex gap="8px" align="center">
              <Select
                size="sm"
                w="80px"
                borderRadius="8px"
                value={pageSize}
                onChange={(e) =>
                  setPagination((prev) => ({
                    ...prev,
                    pageSize: Number(e.target.value),
                    pageIndex: DEFAULT_PAGE_INDEX,
                  }))
                }
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Select>
              <Text fontSize="sm" color="gray.500">
                per page
              </Text>
            </Flex>
            <Flex gap="4px">
              {pages.map((page) => (
                <Button
                  key={page}
                  size="sm"
                  variant={page === pageIndex ? 'brand' : 'ghost'}
                  onClick={() => setPagination((prev) => ({ ...prev, pageIndex: page }))}
                >
                  {page}
                </Button>
              ))}
            </Flex>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  );
}
